import * as tus from "tus-js-client";

import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";

const VIDEOS_BUCKET = "videos";

/** Supabase's resumable upload endpoint speaks plain TUS. */
const TUS_ENDPOINT = `${env.VITE_SUPABASE_URL}/storage/v1/upload/resumable`;

/** Minimum chunk size Supabase will accept on the resumable endpoint. */
const CHUNK_SIZE = 6 * 1024 * 1024;

export class VideoUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "VideoUploadError";
  }
}

function guessExtension(mimeType: string): string {
  if (mimeType.includes("mp4")) return "mp4";
  if (mimeType.includes("quicktime")) return "mov";
  return "webm";
}

/**
 * Upload a participant's recorded pitch to the `videos` bucket under
 * `meetup/<event_id>/<uuid>.<ext>`.
 *
 * Uses TUS (Tus-Resumable v1.0.0) via Supabase Storage's resumable
 * endpoint. Compared to the previous single-PUT upload, this gives us:
 *   - automatic retries with backoff if the connection blips,
 *   - the ability to resume after a tab refresh / app backgrounding
 *     (TUS resumes from the last acknowledged chunk),
 *   - 6 MB chunks, which Supabase's edge prefers and which keep memory
 *     usage low on mobile devices.
 *
 * Participants are anon, so we authenticate the request with the public
 * anon key — the existing INSERT policy on the `videos` bucket gates
 * actual write access by event status.
 */
export async function uploadMeetupVideo(
  eventId: string,
  blob: Blob,
  options?: {
    /** Receives (bytesUploaded, bytesTotal) on each progress event. */
    onProgress?: (bytesUploaded: number, bytesTotal: number) => void;
    /** If set, abort the upload when the controller signals. */
    signal?: AbortSignal;
  },
): Promise<{ path: string }> {
  if (!eventId) throw new VideoUploadError("Missing event id");

  const ext = guessExtension(blob.type);
  const filename = `${crypto.randomUUID()}.${ext}`;
  const path = `meetup/${eventId}/${filename}`;
  // Supabase Storage's allowed_mime_types match basic forms only — a
  // codec-decorated MIME like "video/mp4;codecs=avc1.42001f,mp4a.40.2"
  // (what WebKit's MediaRecorder hands back) gets rejected with 415.
  // Strip the codec parameter for the upload metadata; the actual file
  // bytes don't change.
  const contentType = ((blob.type || "video/webm").split(";")[0] || "video/webm").trim();

  return await new Promise((resolve, reject) => {
    const upload = new tus.Upload(blob, {
      endpoint: TUS_ENDPOINT,
      retryDelays: [0, 1000, 3000, 6000, 12000],
      headers: {
        authorization: `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
        // Same behaviour as `upsert: false` on the legacy upload — the
        // path is a fresh UUID so this is mostly belt-and-braces.
        "x-upsert": "false",
      },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      metadata: {
        bucketName: VIDEOS_BUCKET,
        objectName: path,
        contentType,
        cacheControl: "3600",
      },
      chunkSize: CHUNK_SIZE,
      onError(err: Error) {
        reject(new VideoUploadError(err.message));
      },
      onProgress(bytesUploaded: number, bytesTotal: number) {
        options?.onProgress?.(bytesUploaded, bytesTotal);
      },
      onSuccess() {
        resolve({ path });
      },
    });

    if (options?.signal) {
      const onAbort = () => {
        void upload.abort();
        reject(new VideoUploadError("Upload cancelled"));
      };
      if (options.signal.aborted) {
        onAbort();
        return;
      }
      options.signal.addEventListener("abort", onAbort, { once: true });
    }

    // Resume any half-finished previous upload of the same blob (rare in
    // our flow — each recording gets a new UUID — but free to enable).
    upload.findPreviousUploads().then((previous) => {
      if (previous.length) upload.resumeFromPreviousUpload(previous[0]);
      upload.start();
    });
  });
}

/**
 * Request a short-lived signed URL from the `sign-meetup-video` Edge
 * Function. Works for anon participants — the function verifies that
 * `participantId` is registered in `eventId` before signing.
 */
export async function getSignedMeetupVideoUrl(args: {
  eventId: string;
  participantId: string;
  targetPath: string;
}): Promise<string> {
  const endpoint = `${env.VITE_SUPABASE_URL}/functions/v1/sign-meetup-video`;
  const resp = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: env.VITE_SUPABASE_ANON_KEY,
      Authorization: `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({
      event_id: args.eventId,
      participant_id: args.participantId,
      target_path: args.targetPath,
    }),
  });

  if (!resp.ok) {
    const payload = await resp.json().catch(() => ({}));
    throw new VideoUploadError(payload.error ?? `Sign failed (${resp.status})`);
  }

  const data = (await resp.json()) as { signedUrl: string };
  return data.signedUrl;
}

/**
 * Kick off background AI processing (transcribe → profile). The client does
 * not await the full pipeline — Supabase returns as soon as the function
 * starts. ProfilePage polls `event_participants.profile_json` for the
 * result.
 */
export async function triggerProfileGeneration(participantId: string): Promise<void> {
  const endpoint = `${env.VITE_SUPABASE_URL}/functions/v1/process-meetup-pitch`;
  await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: env.VITE_SUPABASE_ANON_KEY,
      Authorization: `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ participant_id: participantId }),
  }).catch(() => {
    // Fire-and-forget — the participant will still see the video saved;
    // the profile page shows a processing placeholder until profile_json
    // lands.
  });
}

// Imports `supabase` only to keep the same module surface as before
// (other files may transitively rely on it being re-exported via
// upload.ts). Remove if unused.
export { supabase };
